import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame } from 'lucide-react';
import { useActiveFlashDeals } from '@/hooks/useFlashDeals';
import { useLocale } from '@/contexts/LocaleContext';
import { Badge } from '@/components/ui/badge';
import { optimizedImageUrl } from '@/lib/storage';
import { formatKwd } from '@/lib/formatPrice';

function useCountdown(target: string) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = Math.max(0, new Date(target).getTime() - now);
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return { h, m, s, ended: ms === 0 };
}

function DealCard({ deal }: { deal: any }) {
  const { locale } = useLocale();
  const isArabic = locale === 'ar';
  const { h, m, s, ended } = useCountdown(deal.ends_at);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', 'true');
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    tryPlay();
    v.addEventListener('loadedmetadata', tryPlay, { once: true });
    return () => v.removeEventListener('loadedmetadata', tryPlay);
  }, [deal?.activity?.video_url]);


  if (!deal.activity || ended) return null;
  const title = isArabic ? deal.activity.title_ar : (deal.activity.title_en || deal.activity.title_ar);
  const oldPrice = Number(deal.activity.price || 0);
  const newPrice = oldPrice * (1 - deal.discount_percent / 100);
  const videoUrl: string | null = deal.activity.video_url || null;
  const imageUrl: string | null = deal.activity.image_url || null;

  return (
    <Link
      to={`/activities/${deal.activity.id}`}
      className="group block rounded-2xl overflow-hidden bg-white/30 backdrop-blur-2xl border border-white/60 shadow-[0_8px_28px_-8px_rgba(0,0,0,0.18),inset_0_1px_0_0_rgba(255,255,255,0.6)] hover:shadow-elegant transition"
    >
      <div className="relative aspect-[4/5] lg:aspect-[16/10] bg-muted overflow-hidden">
        {videoUrl ? (
          <>
            {imageUrl && !videoReady && (
              <img
                src={optimizedImageUrl(imageUrl, { width: 600, quality: 70 })}
                alt={title}
                aria-hidden
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <video
              ref={videoRef}
              src={videoUrl}
              autoPlay
              loop
              muted
              playsInline
              {...({ 'webkit-playsinline': 'true', 'x5-playsinline': 'true' } as any)}
              disableRemotePlayback
              disablePictureInPicture
              controls={false}
              preload="auto"
              onPlaying={() => setVideoReady(true)}
              onLoadedData={() => setVideoReady(true)}
              className={`w-full h-full object-cover group-hover:scale-105 transition pointer-events-none ${videoReady ? 'opacity-100' : 'opacity-0'}`}
            />
          </>
        ) : imageUrl ? (
          <img
            src={optimizedImageUrl(imageUrl, { width: 600, quality: 75 })}
            alt={title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition"
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
        <Badge className="absolute top-2 start-2 bg-white/10 backdrop-blur-md border border-white/30 text-white gap-1 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.25)] font-bold">
          <Flame className="w-3 h-3" /> {deal.discount_percent}%
        </Badge>
        <div className="absolute bottom-0 inset-x-0 p-3 pt-6 space-y-2 z-10">
          <p className="text-white text-sm md:text-base font-extrabold drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] line-clamp-1 leading-tight">
            {title}
          </p>
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="flex items-baseline leading-none">
              <span className="text-3xl font-black text-white tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                {formatKwd(newPrice)}
              </span>
              <span className="text-sm font-bold text-white ms-1 drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
                {isArabic ? 'د.ك' : 'KWD'}
              </span>
            </div>
            <span className="text-sm text-white/75 line-through decoration-white/50 font-medium drop-shadow">
              {formatKwd(oldPrice)} {isArabic ? 'د.ك' : 'KWD'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-md border border-white/20 px-2.5 py-1 rounded-lg w-fit">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/80 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
            </span>
            <span className="text-[11px] font-bold text-white tabular-nums tracking-wide drop-shadow">
              {isArabic ? 'ينتهي خلال' : 'Ends in'} {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function FlashDealsSection() {
  const { locale } = useLocale();
  const isArabic = locale === 'ar';
  const { data: deals, isLoading } = useActiveFlashDeals();

  if (isLoading) return null;
  const visible = (deals || []).filter(
    (d: any) => d.activity && new Date(d.ends_at).getTime() > Date.now()
  );
  if (visible.length === 0) return null;

  const isFew = visible.length <= 3;

  return (
    <section className="py-8 md:py-12 bg-gradient-to-br from-destructive/5 via-background to-primary/5">
      <div className="container mx-auto px-4">
        <div className="flex items-center gap-2 mb-4">
          <Flame className="w-6 h-6 text-destructive" />
          <h2 className="text-xl md:text-2xl font-bold">
            {isArabic ? 'عروض اليوم 🔥' : "Today's Flash Deals 🔥"}
          </h2>
        </div>
        <div
          className={`flex flex-col gap-4 md:grid md:grid-flow-col md:auto-cols-[45%] md:gap-5 md:overflow-x-auto md:pb-2 md:-mx-4 md:px-4 md:snap-x md:snap-mandatory md:[scrollbar-width:none] md:[&::-webkit-scrollbar]:hidden lg:flex lg:flex-row lg:flex-nowrap lg:gap-5 lg:overflow-visible lg:p-0 lg:m-0 lg:snap-none ${
            isFew ? 'md:justify-center lg:justify-center' : ''
          }`}
        >
          {visible.map((d: any) => (
            <div key={d.id} className="md:snap-start lg:flex-1 lg:min-w-0">
              <DealCard deal={d} />
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
